import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, CheckCircle, Wrench, Snowflake, Flame, Shield, Award, Users, Lightbulb, Star, HardHat } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams({
        'form-name': 'contact',
        'subject': `New Service Request from ${formData.name}`,
        ...formData,
      }).toString();
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!response.ok) {
        throw new Error('Failed to send request');
      }
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us at (253) 501-7893.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: <Flame className="w-12 h-12" />,
      title: 'Heating Services',
      slug: 'heating-services',
      description: 'Expert furnace installation, repair, and maintenance to keep homes warm across Tacoma, Puyallup, and Pierce County.',
      image: 'https://images.unsplash.com/photo-1758545814875-c2f0a0c89943?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxIVkFDJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTI2NDg2MHww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Snowflake className="w-12 h-12" />,
      title: 'Air Conditioning',
      slug: 'air-conditioning',
      description: 'Professional AC installation, repair, and tune-ups serving Puyallup, Tacoma, and surrounding areas in Pierce County, WA.',
      image: 'https://images.pexels.com/photos/24828656/pexels-photo-24828656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: 'Repair & Maintenance',
      slug: 'repair-maintenance',
      description: 'Fast, reliable HVAC repairs and preventive maintenance from a licensed contractor in Washington state.',
      image: 'https://images.pexels.com/photos/32497161/pexels-photo-32497161.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: <HardHat className="w-12 h-12" />,
      title: 'New Construction HVAC',
      slug: 'new-construction',
      description: 'Complete HVAC system design and installation for new residential and commercial construction projects in Pierce County.',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bmV3JTIwY29uc3RydWN0aW9ufGVufDB8fDB8fHww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Clock className="w-12 h-12" />,
      title: 'Emergency Service',
      slug: 'emergency-service',
      description: '24/7 emergency HVAC services in Pierce County, WA. We\'re always here when you need us most.',
      image: 'https://images.unsplash.com/photo-1662556153586-223039dc8152?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxIVkFDJTIwdGVjaG5pY2lhbnxlbnwwfHx8fDE3NzEyNjQ4NTZ8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Commercial HVAC',
      slug: 'commercial-hvac',
      description: 'Comprehensive commercial HVAC solutions for businesses of all sizes in Tacoma, Puyallup, and Pierce County.',
      image: 'https://images.unsplash.com/photo-1751486289950-5c4898a4c773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxIVkFDJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3MTI2NDg2MHww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Residential HVAC',
      slug: 'residential-hvac',
      description: 'Complete residential HVAC and remodeling services in Tacoma and Puyallup, tailored to your home comfort needs.',
      image: 'https://images.pexels.com/photos/7347538/pexels-photo-7347538.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  const whyChooseUs = [
    { icon: <Award className="w-8 h-8" />, title: 'Licensed & Insured', description: 'Licensed contractor in Washington state — fully certified professionals you can trust' },
    { icon: <Clock className="w-8 h-8" />, title: '24/7 Availability', description: 'Emergency HVAC services available anytime in Pierce County' },
    { icon: <CheckCircle className="w-8 h-8" />, title: 'Quality Guaranteed', description: ' 100% satisfaction on every job in Tacoma, Puyallup, and beyond' },
    { icon: <Users className="w-8 h-8" />, title: 'Expert Technicians', description: 'Experienced and trained HVAC professionals serving all of Pierce County, WA' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="HVAC Contractor, Heating & Air Conditioning | Tacoma, Puyallup, Pierce County WA"
        description="Taking Over Contracting Inc — Licensed HVAC contractor in Pierce County, WA. Expert heating, air conditioning, mechanical repairs, new construction HVAC, furnace installation, and 24/7 emergency service in Tacoma, Puyallup, Lakewood. Call (253) 501-7893."
        keywords="HVAC contractor Pierce County WA, heating and air conditioning Tacoma, HVAC repair Puyallup, furnace installation Pierce County, AC repair Tacoma, new construction HVAC, mechanical contractor Puyallup, emergency HVAC service, commercial HVAC Tacoma, residential heating Puyallup, heat pump installation, ductless mini split"
        canonicalPath="/"
      />
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Taking Over Contracting Inc</h1>
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/tips" className="hover:text-red-500 transition-colors font-semibold">HVAC Tips</Link>
            <Link to="/reviews" className="hover:text-red-500 transition-colors font-semibold flex items-center space-x-1">
              <Star className="w-4 h-4" />
              <span>Leave a Review</span>
            </Link>
            <a href="tel:2535017893" className="flex items-center space-x-2 hover:text-red-500 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">(253) 501-7893</span>
            </a>
            <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-red-600 hover:bg-red-700 text-white"
              >Get a Quote</Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              Licensed General Contractor in Pierce County, WA
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-black leading-tight">
              Your Comfort Is Our Priority
            </h2>
            <p className="text-xl text-gray-700">
              Licensed contractor in Washington providing expert HVAC services, remodeling, heating, and cooling for residential and commercial properties in Tacoma, Puyallup, Lakewood, and throughout Pierce County, WA.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.location.href = 'tel:2535017893'}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: (253) 501-7893
              </Button>
              <Button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-black hover:bg-gray-800 text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                Request Service
              </Button>
            </div>
            <div>
              <Button
                asChild
                className="bg-white hover:bg-gray-100 text-red-600 border-2 border-red-600 text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
              >
                <Link to="/tips">
                  <Lightbulb className="w-5 h-5 mr-2" />
                  HVAC Tips
                </Link>
              </Button>
            </div>
            <div className="flex items-center space-x-6 pt-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-red-600" />
                <span className="text-gray-700 font-medium">Licensed & Insured in Washington</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-6 h-6 text-red-600" />
                <span className="text-gray-700 font-medium">24/7 Emergency</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/27134985/pexels-photo-27134985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="HVAC Equipment"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-xl">
              <div className="text-4xl font-bold">15+</div>
              <div className="text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive HVAC and contracting solutions for all your heating, cooling, and remodeling needs in Pierce County, WA
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-red-600 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="text-red-600 bg-white p-4 rounded-full">
                      {service.icon}
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                  >
                    <Link to={`/services/${service.slug}`}>Learn More</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experience the difference of working with a trusted, licensed general contractor in Pierce County, WA
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index} 
                className="text-center p-8 rounded-xl bg-gray-900 hover:bg-gray-800 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">General Contractor Serving Tacoma, Puyallup & All of Pierce County</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            As a licensed contractor in Washington, we proudly provide HVAC services, remodeling, and general contracting to residential and commercial customers throughout Pierce County, WA. Whether you need heating repair in Tacoma, AC installation in Puyallup, or a remodeling contractor in Lakewood, we're here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8 max-w-3xl mx-auto">
            {['Puyallup', 'Tacoma', 'Lakewood', 'Spanaway', 'Bonney Lake', 'University Place', 'Graham', 'Frederickson', 'Sumner', 'Edgewood', 'Milton', 'Fife', 'Pacific', 'South Hill', 'Parkland'].map((city) => (
              <span key={city} className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
                {city}, WA
              </span>
            ))}
          </div>
          <div className="flex justify-center items-center space-x-3 text-lg">
            <MapPin className="w-6 h-6 text-red-600" />
            <span className="text-gray-700 font-medium">1723 13th Ave Ct NW, Puyallup, WA 98371</span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Get In Touch</h2>
              <p className="text-xl text-gray-600 mb-8">
                Ready to schedule service or have questions? Contact your trusted general contractor in Pierce County, WA for fast, professional HVAC and remodeling services.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-black mb-1">Phone</h3>
                    <a href="tel:2535017893" className="text-gray-600 hover:text-red-600 transition-colors">
                      (253) 501-7893
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-black mb-1">Email</h3>
                    <a href="mailto:josh.takingovercontracting@gmail.com" className="text-gray-600 hover:text-red-600 transition-colors">
                      josh.takingovercontracting@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-black mb-1">Address</h3>
                    <p className="text-gray-600">
                      1723 13th Ave Ct NW<br />
                      Puyallup, WA 98371
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-black mb-1">Hours</h3>
                    <p className="text-gray-600">
                      24/7 Emergency Service Available<br />
                      Regular Hours: Mon-Fri 8AM-6PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card className="shadow-xl border-2 border-gray-100">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-black mb-6">Request Service</h3>
                  <form name="contact" method="POST" onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="form-name" value="contact" />
                    <input type="hidden" name="subject" value="New Service Request" />
                    <p style={{ display: 'none' }}>
                      <label>Don't fill this: <input name="bot-field" /></label>
                    </p>
                    <div>
                      <Input
                        type="text"
                        name="name"
                        placeholder="Your Name *"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        name="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>
                    <div>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      >
                        <option value="">Select Service *</option>
                        <option value="heating">Heating Services</option>
                        <option value="cooling">Air Conditioning</option>
                        <option value="repair">Repair & Maintenance</option>
                        <option value="new-construction">New Construction HVAC</option>
                        <option value="emergency">Emergency Service</option>
                        <option value="commercial">Commercial HVAC</option>
                        <option value="residential">Residential HVAC</option>
                      </select>
                    </div>
                    <div>
                      <Textarea
                        name="message"
                        placeholder="Tell us about your HVAC needs..."
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Request'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Taking Over Contracting Inc</h3>
              </div>
              <p className="text-gray-400">
                Your trusted licensed general contractor in Pierce County, WA. Professional HVAC, heating, cooling, and remodeling services in Tacoma, Puyallup, and surrounding areas.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-red-500 transition-colors">Home</a></li>
                <li><a href="#" className="hover:text-red-500 transition-colors">Services</a></li>
                <li><Link to="/tips" className="hover:text-red-500 transition-colors">HVAC Tips</Link></li>
                <li><Link to="/reviews" className="hover:text-red-500 transition-colors">Reviews</Link></li>
                <li><a href="#contact" className="hover:text-red-500 transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:2535017893" className="hover:text-red-500 transition-colors">(253) 501-7893</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:josh.takingovercontracting@gmail.com" className="hover:text-red-500 transition-colors text-sm">
                    josh.takingovercontracting@gmail.com
                  </a>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span className="text-sm">1723 13th Ave Ct NW<br />Puyallup, WA 98371</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">UBI# 60589170 &nbsp;|&nbsp; License# TAKINOC755K2</p>
            <p>&copy; {new Date().getFullYear()} Taking Over Contracting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
